import { CartLinesUpdateEvent } from '@shopify/events';

class CartUpsellCarousel extends HTMLElement {
  constructor() {
    super();
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleInteraction = this.handleInteraction.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-cart-upsell-viewport]');
    this.previousButton = this.querySelector('[data-cart-upsell-previous]');
    this.nextButton = this.querySelector('[data-cart-upsell-next]');
    this.status = this.querySelector('[data-cart-upsell-status]');
    this.controls = this.querySelector('[data-cart-upsell-controls]');

    if (!(this.viewport instanceof HTMLElement)) return;

    this.applyRecentlyViewedMode();
    this.previousButton?.addEventListener('click', () => this.scrollPage(-1));
    this.nextButton?.addEventListener('click', () => this.scrollPage(1));
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    this.addEventListener('click', this.handleInteraction, true);

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.viewport);

    requestAnimationFrame(() => this.update());
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    this.removeEventListener('click', this.handleInteraction, true);
    this.resizeObserver?.disconnect();

    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  applyRecentlyViewedMode() {
    if (this.dataset.upsellSource !== 'recently_viewed') return;

    let viewedProducts = [];
    try {
      viewedProducts = JSON.parse(localStorage.getItem('viewedProducts') || '[]').map(String);
    } catch (_error) {
      viewedProducts = [];
    }

    if (!viewedProducts.length) return;

    const cards = Array.from(this.querySelectorAll('[data-cart-upsell-card]'));
    const viewedCards = cards
      .filter((card) => viewedProducts.includes(String(card.dataset.productId)))
      .sort(
        (a, b) =>
          viewedProducts.indexOf(String(a.dataset.productId)) -
          viewedProducts.indexOf(String(b.dataset.productId))
      );

    if (!viewedCards.length) return;

    const track = this.querySelector('.cart-upsell__track');
    if (!(track instanceof HTMLElement)) return;

    track.replaceChildren(...viewedCards);
  }

  async handleInteraction(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(
      '[data-cart-upsell-action], [data-cart-upsell-product-trigger]'
    );

    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    event.preventDefault();
    event.stopPropagation();

    const card = trigger.closest('.cart-upsell__card');
    if (!(card instanceof HTMLElement)) return;

    const shouldAddDirectly = trigger.matches('[data-cart-upsell-action]');

    const actionButton =
      card.querySelector('[data-cart-upsell-action]') ||
      card.querySelector('.cart-upsell__quick-add .quick-add__button--add:not([style*="display: none"])') ||
      card.querySelector('.cart-upsell__quick-add .quick-add__button--choose') ||
      card.querySelector('.cart-upsell__quick-add .quick-add__button');

    if (!(actionButton instanceof HTMLButtonElement)) return;

    if (shouldAddDirectly) {
      await this.addDirectly(card, actionButton);
      return;
    }

    await this.runQuickAdd(card, actionButton, event);
  }

  async addDirectly(card, button) {
    if (this.addPending || button.disabled) return;
    this.addPending = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const feedback = this.querySelector('[data-cart-upsell-feedback]');
    if (feedback) feedback.textContent = '';

    try {
      const input = card.querySelector('input[name="id"]');
      const variantId = input?.value || button.dataset.variantId;
      const quantity = Number(card.querySelector('input[name="quantity"]')?.value || button.dataset.quantity || 1);
      if (!variantId) throw new Error('Não foi possível identificar a variante deste produto.');
      const root = window.Shopify?.routes?.root || '/';
      const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'))
        .map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }], sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Não foi possível adicionar o produto.');

      const promise = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(async (cartResponse) => {
          if (!cartResponse.ok) throw new Error('O produto foi adicionado, mas não foi possível atualizar o carrinho.');
          const cart = await cartResponse.json();
          return {
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, sections: result.sections, source: 'cart-upsell', itemCount: quantity, didError: false },
          };
        });
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product', lines: [{ merchandiseId: variantId, quantity }], promise,
      }));
      await promise;
    } catch (error) {
      if (feedback) feedback.textContent = error.message || 'Não foi possível adicionar o produto. Tente novamente.';
      console.error('[cart-upsell] Direct add failed:', error);
    } finally {
      this.addPending = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async runQuickAdd(card, actionButton, event, shouldAddDirectly = false) {
    const quickAdd = card.querySelector('quick-add-component');
    if (!(quickAdd instanceof HTMLElement)) return;
    if (actionButton.disabled) return;

    actionButton.setAttribute('aria-busy', 'true');

    try {
      await Promise.all([
        customElements.whenDefined('quick-add-component'),
        customElements.whenDefined('product-card'),
        customElements.whenDefined('product-form-component'),
      ]);

      const mode = quickAdd.dataset.quickAddButton || actionButton.dataset.cartUpsellMode || 'choose';

      if (shouldAddDirectly || mode === 'add') {
        const nativeAddButton = quickAdd.querySelector('.quick-add__button--add');
        const productForm = nativeAddButton?.closest('form');

        if (
          nativeAddButton instanceof HTMLButtonElement &&
          productForm instanceof HTMLFormElement &&
          !nativeAddButton.disabled
        ) {
          productForm.requestSubmit(nativeAddButton);
        }

        return;
      }

      const dialog = document.getElementById('quick-add-dialog');
      if (dialog) {
        dialog.classList.add('quick-add-dialog--cart-upsell');
        dialog.addEventListener('dialog:close', () => dialog.classList.remove('quick-add-dialog--cart-upsell'), {
          once: true,
        });
      }

      if (typeof quickAdd.handleClick === 'function') {
        await quickAdd.handleClick(event);
        return;
      }

      const nativeChooseButton = quickAdd.querySelector('.quick-add__button--choose');
      if (nativeChooseButton instanceof HTMLButtonElement) {
        nativeChooseButton.click();
      }
    } catch (error) {
      console.error('[cart-upsell] Quick add failed:', error);
    } finally {
      window.setTimeout(() => {
        actionButton.removeAttribute('aria-busy');
      }, 450);
    }
  }

  getPageOffsets() {
    if (!(this.viewport instanceof HTMLElement)) return [0];

    const cards = Array.from(this.querySelectorAll('.cart-upsell__card'));
    const offsets = cards
      .map((card) => Math.round(card.offsetLeft))
      .filter((offset, index, all) => index === 0 || Math.abs(offset - all[index - 1]) > 2);

    return offsets.length ? offsets : [0];
  }

  getCurrentPageIndex(offsets = this.getPageOffsets()) {
    if (!(this.viewport instanceof HTMLElement)) return 0;

    const current = Math.max(0, this.viewport.scrollLeft);
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    offsets.forEach((offset, index) => {
      const distance = Math.abs(offset - current);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  scrollPage(direction) {
    if (!(this.viewport instanceof HTMLElement)) return;

    const offsets = this.getPageOffsets();
    const currentIndex = this.getCurrentPageIndex(offsets);
    const targetIndex = Math.min(offsets.length - 1, Math.max(0, currentIndex + direction));

    this.viewport.scrollTo({
      left: offsets[targetIndex],
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  handleScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => this.update());
  }

  handleResize() {
    this.update();
  }

  update() {
    if (!(this.viewport instanceof HTMLElement)) return;

    const offsets = this.getPageOffsets();
    const pageCount = offsets.length;
    const currentIndex = this.getCurrentPageIndex(offsets);
    const hasOverflow = pageCount > 1;

    this.controls?.toggleAttribute('hidden', !hasOverflow);

    if (this.previousButton instanceof HTMLButtonElement) {
      this.previousButton.disabled = currentIndex <= 0;
    }

    if (this.nextButton instanceof HTMLButtonElement) {
      this.nextButton.disabled = currentIndex >= pageCount - 1;
    }

    if (this.status) {
      this.status.textContent = `${currentIndex + 1} / ${pageCount}`;
    }
  }
}

if (!customElements.get('cart-upsell-carousel')) {
  customElements.define('cart-upsell-carousel', CartUpsellCarousel);
}
